import { useState } from 'react'
import { UserProfile as UserProfileType } from '../../types'
import { User, Mail, Phone, Calendar, Users, Shield, Edit2, Save, X } from 'lucide-react'
import { useForm } from 'react-hook-form'

function HeadshotAvatar({ user }: { user: UserProfileType }) {
  const [imgError, setImgError] = useState(false)

  if (user.headshotUrl && !imgError) {
    return (
      <img
        src={user.headshotUrl}
        alt={`${user.firstName} ${user.lastName}`}
        className="h-14 w-14 rounded-full object-cover border-2 border-white border-opacity-40"
        onError={() => setImgError(true)}
      />
    )
  }

  return (
    <div className="bg-white bg-opacity-20 p-3 rounded-full">
      <User className="h-8 w-8" />
    </div>
  )
}

interface UserProfileProps {
  user: UserProfileType
  onUpdateProfile: (updates: Partial<UserProfileType>) => Promise<boolean>
  isLoading: boolean
}

interface EditableFields {
  emergencyContact: {
    name: string
    phone: string
    relationship: string
  }
}

export default function UserProfile({ user, onUpdateProfile, isLoading }: UserProfileProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<EditableFields>({
    defaultValues: {
      emergencyContact: user.emergencyContact
    }
  })

  const handleEdit = () => {
    setIsEditing(true)
    setMessage(null)
    reset({ emergencyContact: user.emergencyContact })
  }

  const handleCancel = () => {
    setIsEditing(false)
    setMessage(null)
    reset({ emergencyContact: user.emergencyContact })
  }

  const handleSave = async (data: EditableFields) => {
    setMessage(null)
    const success = await onUpdateProfile({
      emergencyContact: data.emergencyContact
    })
    
    if (success) {
      setMessage({
        type: 'success',
        text: 'Profile updated successfully!'
      })
      setIsEditing(false)
    } else {
      setMessage({
        type: 'error',
        text: 'Failed to update profile. Please try again.'
      })
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'photo_admin':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200'
    }
  }

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrator'
      case 'photo_admin':
        return 'Photo Admin'
      default:
        return 'Pilot'
    }
  }

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-fwo-blue to-blue-600 px-6 py-8 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <HeadshotAvatar user={user} />
            <div>
              <h1 className="text-2xl font-bold">
                {user.firstName} {user.lastName}
              </h1>
              <p className="text-blue-100">Competition Pilot • {user.competitionYear}</p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full border text-sm font-medium ${getRoleBadgeColor(user.role)}`}>
            <Shield className="h-4 w-4 inline mr-1" />
            {getRoleDisplayName(user.role)}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {message && (
          <div className={`mb-6 p-3 rounded-md ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <p className="text-sm">{message.text}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(handleSave)}>
          {/* Basic Information */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Mail className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Email</p>
                  <p className="text-sm text-gray-900">{user.email}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Competition Year</p>
                  <p className="text-sm text-gray-900">{user.competitionYear}</p>
                </div>
              </div>

              {user.busGroup && (
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Users className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Bus Group</p>
                    <p className="text-sm text-gray-900">{user.busGroup}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Emergency Contact</h2>
              {!isEditing && (
                <button
                  type="button"
                  onClick={handleEdit}
                  className="flex items-center space-x-2 text-fwo-blue hover:text-blue-700 transition-colors"
                >
                  <Edit2 className="h-4 w-4" />
                  <span className="text-sm">Edit</span>
                </button>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Name
                </label>
                {isEditing ? (
                  <input
                    {...register('emergencyContact.name', {
                      required: 'Contact name is required'
                    })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:border-fwo-blue"
                    placeholder="Emergency contact name"
                  />
                ) : (
                  <p className="text-sm text-gray-900 p-3 bg-gray-50 rounded-lg">
                    {user.emergencyContactName || user.emergencyContact?.name}
                  </p>
                )}
                {errors.emergencyContact?.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.emergencyContact.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                {isEditing ? (
                  <input
                    {...register('emergencyContact.phone', {
                      required: 'Phone number is required',
                      pattern: {
                        value: /^[+]?[1-9][\d]{0,15}$/,
                        message: 'Invalid phone number format'
                      }
                    })}
                    type="tel"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:border-fwo-blue"
                    placeholder="+1234567890"
                  />
                ) : (
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <p className="text-sm text-gray-900">{user.emergencyContactPhone || user.emergencyContact?.phone}</p>
                  </div>
                )}
                {errors.emergencyContact?.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.emergencyContact.phone.message}</p>
                )}
              </div>

            </div>

            {isEditing && (
              <div className="flex space-x-3 mt-6">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex items-center space-x-2 bg-fwo-blue text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Save className="h-4 w-4" />
                  <span>{isLoading ? 'Saving...' : 'Save Changes'}</span>
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex items-center space-x-2 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition-colors"
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
              </div>
            )}
          </div>

          {/* Account Information */}
          <div className="border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Account Created</p>
                <p className="text-gray-900 font-medium">
                  {new Date(user.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Last Updated</p>
                <p className="text-gray-900 font-medium">
                  {new Date(user.updatedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}